/* eslint-disable max-lines */

/* eslint-disable react/jsx-no-literals */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Tooltip, useTheme } from '@mui/material';
import CircleIconSrc from 'src/assets/icons/circle.svg';
import CircleCheckIconSrc from 'src/assets/icons/circle-check.svg';
import TriangleIconSrc from 'src/assets/icons/triangle.svg';
import TriangleCheckIconSrc from 'src/assets/icons/triangle-check.svg';
import styled, { css } from 'styled-components';

import AddNewCircleSVG from '@/assets/icons/add-new-circle.svg';
import AddNewSquareSVG from '@/assets/icons/add-new-square.svg';
import AddNewTriangleSVG from '@/assets/icons/add-new-triangle.svg';
import SquareIconSrc from '@/assets/icons/square.svg';
import SquareCheckIconSrc from '@/assets/icons/square-check.svg';
import {
  createFilterOption,
  getFiltersByRoadmapId,
  getOptionsByFilterId,
  reorderFilterOptions,
} from '@/features/canvas/api';
import { FilterMultiOption } from '@/features/canvas/components/FilterMultiOption';
import { FilterSingleOption } from '@/features/canvas/components/FilterSingleOption';
import { FILTER_TITLE_MAX_LENGTH, TITLE_DISPLAY_LIMIT } from '@/features/canvas/constants';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { FilterTypes } from '@/features/canvas/types/filter';
import { getColorForSize } from '@/features/canvas/utils/size-to-color';
import { needsTruncation, truncateText } from '@/features/canvas/utils/text-utils';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

const isSizeFilter = (options?: components['schemas']['FilterOption'][]): boolean => {
  if (!options || options.length === 0) return false;
  return options.every(option => typeof option.size === 'number');
};

type FilterOptionWithSort = components['schemas']['FilterOption'] & { sort_order: number };

// eslint-disable-next-line max-statements
const FiltersAccordionSubContent = ({
  filter,
  handleEdit,
  roadmapId,
}: {
  filter: components['schemas']['Filter'];
  handleEdit: (filter: components['schemas']['Filter']) => Promise<void>;
  roadmapId: number;
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>(filter.title);
  const [originalValue, setOriginalValue] = useState<string>(filter.title);

  useEffect(() => {
    setInputValue(filter.title);
    setOriginalValue(filter.title);
  }, [filter.title]);

  const theme = useTheme();
  const { showToast } = useShowToast();
  const queryClient = useQueryClient();
  const { setIsSidebarContentLoading, updateLastSavedTime } = useEditorContext();

  const { data: options } = useQuery<FilterOptionWithSort[]>(
    [getOptionsByFilterId.name, filter.id],
    () => {
      return getOptionsByFilterId(filter.id!) as Promise<FilterOptionWithSort[]>;
    },
    {
      enabled: Boolean(filter.id),
    }
  );

  const { reorderedFilterOptions, setReorderedFilterOptions } = useEditorContext();

  useEffect(() => {
    if (!filter.id || !options?.length) return;

    const filterId = filter.id as number;

    setReorderedFilterOptions(prev => {
      const current = prev[filterId];
      const isSameLength = current?.length === options.length;

      if (isSameLength) return prev;

      const sortedOptions = options
        .map((opt, i) => ({
          ...opt,
          sort_order: typeof opt.sort_order === 'number' ? opt.sort_order : i,
        }))
        .sort((a, b) => a.sort_order - b.sort_order);

      return {
        ...prev,
        [filterId]: sortedOptions,
      };
    });
  }, [filter.id, options, setReorderedFilterOptions]);

  const sortableOptions = reorderedFilterOptions[filter.id!] || [];

  const [activeOption, setActiveOption] = useState<components['schemas']['FilterOption'] | null>(
    null
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const { mutateAsync: createOption } = useMutation(
    async (option: components['schemas']['FilterOption']) => {
      try {
        await createFilterOption({ filterId: filter.id!, payload: option });
        updateLastSavedTime();
        showToast('success', t('editor.sidebar.filter.create_option_success'));
      } catch (e) {
        showToast('error', t('editor.sidebar.filter.create_option_error'));
      }
    }
  );

  const { mutateAsync: persistFilterOptionOrder } = useMutation(
    async (optionsToPersist: { id: number; sort_order: number }[]) => {
      await reorderFilterOptions(filter.id!, optionsToPersist);
      updateLastSavedTime();
      showToast('success', t('editor.sidebar.filter.reorder_success'));
    },
    {
      onError: () => {
        showToast('error', t('editor.sidebar.filter.reorder_error'));
      },
    }
  );

  const handleShapeEdit = async (shape: 'circle' | 'square' | 'triangle') => {
    setIsSidebarContentLoading(true);

    const updatedFilter = { ...filter, shape };
    await handleEdit(updatedFilter);

    setIsSidebarContentLoading(false);
  };

  const isCustomFilter = (value: string) => {
    return (
      value !== FilterTypes.PRIORITY &&
      value !== FilterTypes.PROGRESS_STATUS &&
      value !== FilterTypes.RAG &&
      value !== FilterTypes.SIZE
    );
  };

  const handleTitleEdit = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length <= FILTER_TITLE_MAX_LENGTH) {
      setInputValue(value);
    }
  };

  const handleTitleInputBlur = async () => {
    if (!inputValue.trim()) {
      setInputValue(originalValue);
      showToast('error', t('editor.sidebar.filter.filter_title_empty_error'));
      return;
    }

    if (!isCustomFilter(inputValue)) {
      setInputValue(originalValue);
      showToast('error', t('editor.sidebar.filter.filter_title_in_used'));
      return;
    }

    if (inputValue === originalValue) {
      return;
    }

    setIsSidebarContentLoading(true);
    await handleEdit({ ...filter, title: inputValue });
    setOriginalValue(inputValue);
    setIsSidebarContentLoading(false);
  };

  const handleTitleInputKeyDown = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      await handleTitleInputBlur();
    } else if (event.key === 'Escape') {
      setInputValue(originalValue);
    }
  };

  const handleOptionAdd = async () => {
    const isSize = isSizeFilter(options);
    const defaultSize = 3;

    const currentOptions = reorderedFilterOptions[filter.id!] || [];

    const sortOrder = currentOptions.length;

    const newOption: components['schemas']['FilterOption'] = {
      title: t('editor.sidebar.filter.new_option_title'),
      color: isSize ? getColorForSize(defaultSize) : theme.palette.brand.corePPTHeadlines,
      ...(isSize ? { size: defaultSize } : {}),
      sort_order: sortOrder,
    };

    setIsSidebarContentLoading(true);

    await createOption(newOption);
    await queryClient.invalidateQueries([getOptionsByFilterId.name, filter.id]);
    await queryClient.invalidateQueries([getFiltersByRoadmapId.name, roadmapId]);

    setIsSidebarContentLoading(false);
  };

  const getAddIcon = () => {
    let addIcon = AddNewCircleSVG;
    if (filter.shape === 'triangle') {
      addIcon = AddNewTriangleSVG;
    } else if (filter.shape === 'square') {
      addIcon = AddNewSquareSVG;
    }

    return (
      <StyledAddNewSVG
        src={addIcon}
        onClick={handleOptionAdd}
        $isTriangle={filter.shape === 'triangle'}
      />
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    const dragged = options?.find(opt => opt.id === event.active.id);
    setActiveOption(dragged || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveOption(null);
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortableOptions.findIndex(opt => opt.id === active.id);
      const newIndex = sortableOptions.findIndex(opt => opt.id === over?.id);
      const reordered = arrayMove(sortableOptions, oldIndex, newIndex);

      setReorderedFilterOptions(prev => ({
        ...prev,
        [filter.id!]: reordered,
      }));

      const optionsToPersist = reordered.map((opt, index) => ({
        id: opt.id!,
        sort_order: index,
      }));

      await persistFilterOptionOrder(optionsToPersist);
      await queryClient.invalidateQueries([getOptionsByFilterId.name, filter.id]);
    }
  };

  // Display title with truncation and tooltip
  const displayTitle = truncateText(filter.title, TITLE_DISPLAY_LIMIT);
  const showTitleTooltip = needsTruncation(filter.title, TITLE_DISPLAY_LIMIT);

  return (
    <div>
      <SelectTypeText>
        {filter.selection === 'single'
          ? t('editor.sidebar.filter.selection_type_single', { filterTitle: displayTitle })
          : t('editor.sidebar.filter.selection_type_multi', { filterTitle: displayTitle })}
      </SelectTypeText>
      {(isCustomFilter(filter.title) || isSizeFilter(options)) && (
        <TitleWrapper>
          <AccordionSubContentText>{t('editor.sidebar.filter.title')}</AccordionSubContentText>
          <TitleInputWrapper>
            {showTitleTooltip ? (
              <Tooltip title={filter.title} placement="top" arrow>
                <TitleInput
                  type="text"
                  value={inputValue}
                  onChange={handleTitleEdit}
                  onBlur={handleTitleInputBlur}
                  onKeyDown={handleTitleInputKeyDown}
                  maxLength={FILTER_TITLE_MAX_LENGTH}
                />
              </Tooltip>
            ) : (
              <TitleInput
                type="text"
                value={inputValue}
                onChange={handleTitleEdit}
                onBlur={handleTitleInputBlur}
                onKeyDown={handleTitleInputKeyDown}
                maxLength={FILTER_TITLE_MAX_LENGTH}
              />
            )}
            <CharacterCount>
              {inputValue.length}/{FILTER_TITLE_MAX_LENGTH}
            </CharacterCount>
          </TitleInputWrapper>
        </TitleWrapper>
      )}
      <OptionWrapper>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableOptions.map(o => o.id!)}
            strategy={verticalListSortingStrategy}
          >
            {sortableOptions.map(option => (
              <SortableOptionItem key={option.id} id={option.id!}>
                {filter.selection === 'single' ? (
                  <FilterSingleOption
                    option={option}
                    filterId={filter.id!}
                    roadmapId={roadmapId}
                    filterShape={filter.shape!}
                  />
                ) : (
                  <FilterMultiOption
                    option={option}
                    filterId={filter.id!}
                    roadmapId={roadmapId}
                    filterShape={filter.shape!}
                  />
                )}
              </SortableOptionItem>
            ))}
          </SortableContext>

          <DragOverlay>
            {activeOption &&
              (filter.selection === 'single' ? (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      cursor: 'grabbing',
                      zIndex: 1000,
                      content: "''",
                    }}
                  />
                  <FilterSingleOption
                    option={activeOption}
                    filterId={filter.id!}
                    roadmapId={roadmapId}
                    filterShape={filter.shape!}
                  />
                </>
              ) : (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      cursor: 'grabbing',
                      zIndex: 1000,
                      content: "''",
                    }}
                  />
                  <FilterMultiOption
                    option={activeOption}
                    filterId={filter.id!}
                    roadmapId={roadmapId}
                    filterShape={filter.shape!}
                  />
                </>
              ))}
          </DragOverlay>
        </DndContext>
      </OptionWrapper>
      {getAddIcon()}
      {!isSizeFilter(options) && (
        <ShapeSelectWrapper>
          <AccordionSubContentText>
            {t('editor.sidebar.filter.selection_shape')}
          </AccordionSubContentText>
          <ShapeImagesWrapper>
            {filter.shape === 'circle' ? (
              <StyledShapeSVGCheck src={CircleCheckIconSrc} />
            ) : (
              <StyledShapeSVG src={CircleIconSrc} onClick={() => handleShapeEdit('circle')} />
            )}
            {filter.shape === 'square' ? (
              <StyledShapeSVGCheck src={SquareCheckIconSrc} />
            ) : (
              <StyledShapeSVG src={SquareIconSrc} onClick={() => handleShapeEdit('square')} />
            )}
            {filter.shape === 'triangle' ? (
              <StyledShapeSVGCheck src={TriangleCheckIconSrc} />
            ) : (
              <StyledShapeSVG src={TriangleIconSrc} onClick={() => handleShapeEdit('triangle')} />
            )}
          </ShapeImagesWrapper>
        </ShapeSelectWrapper>
      )}
    </div>
  );
};

const SortableOptionItem = ({ id, children }: { children: React.ReactElement; id: number }) => {
  const { setNodeRef, attributes, listeners, transform, transition } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {React.cloneElement(children, { attributes, listeners })}
    </div>
  );
};

const SelectTypeText = styled.div`
  font-size: 1.2rem;
  padding: 0 1.5rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
`;

const TitleWrapper = styled.div`
  margin: 0 1.5rem;
  margin-bottom: 2rem;
`;

const TitleInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleInput = styled.input`
  outline: none;
  width: 100%;
  border-radius: 0.4rem;
  border: 1px solid ${({ theme }) => theme.palette.brand.backgroundDialog};
  padding: 0 8px;
  font-size: 13px;

  &:focus {
    border: 1px solid ${({ theme }) => theme.palette.brand.textSecondary};
  }
`;

const CharacterCount = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
  text-align: right;
  margin-top: 4px;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 1.5rem;
`;

const StyledAddNewSVG = styled(SVG)<{ $isTriangle: boolean }>`
  width: 10rem;
  height: 2.5rem;
  cursor: pointer;
  margin: 1rem 0.5rem;

  ${({ $isTriangle }) =>
    $isTriangle &&
    css`
      .st0 path {
        fill: ${({ theme }) => theme.palette.brand.textPrimary}!important;
      }

      polygon {
        fill: ${({ theme }) => theme.palette.brand.textPrimary}!important;
      }

      #Polygon_102 .st2 {
        fill-opacity: 0;
      }

      #Polygon_102 .st1 {
        fill: ${({ theme }) => theme.palette.brand.textPrimary}!important;
      }
    `}

  ${({ $isTriangle }) =>
    !$isTriangle &&
    css`
      path {
        fill: ${({ theme }) => theme.palette.brand.textPrimary}!important;
      }

      polygon {
        fill: ${({ theme }) => theme.palette.brand.textPrimary}!important;
      }

      circle {
        fill-opacity: 0 !important;
      }
    `}
`;

const ShapeSelectWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 1.5rem;
  align-items: center;
`;

const ShapeImagesWrapper = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: center;
`;

const StyledShapeSVG = styled(SVG)`
  width: 1.8rem;
  height: 1.8rem;
  cursor: pointer;
  margin: 0 0.5rem;
`;

const StyledShapeSVGCheck = styled(SVG)`
  width: 2.8rem;
  height: 2.8rem;
  cursor: pointer;

  g,
  polygon,
  rect,
  path {
    fill: ${({ theme }) => theme.palette.brand.editorTabPrimary}!important;
  }
`;

const AccordionSubContentText = styled.div`
  font-size: 1.3rem;
  padding-bottom: 0.5rem;
`;

export { FiltersAccordionSubContent };
